import { Select, Table, TableColumnsType } from 'antd';
import moment from 'moment';
import QueryString from 'qs';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { getLandings } from '../../../api/landing.api';

import Button from '../../../components/Button';
import ErrorResponse from '../../../components/ErrorResponse';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Loader from '../../../components/Loader';
import Pagination from '../../../components/Pagination';
import useAuth from '../../../hooks/useAuth';
import useAxios from '../../../hooks/useAxios';
import config from '../../../lib/config';
import { IGetLandings, ILandingItem } from '../../../models/landing.model';
import { RootState } from '../../../store/rootReducer';

const HomepageLanding = () => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const { serverDate } = useSelector((state: RootState) => state.serverDate);

  const { data: res, isLoading, error, execute } = useAxios<IGetLandings>();

  const [field, setField] = useState<string>('title');
  const [keyword, setKeyword] = useState<string>('');
  const [page, setPage] = useState(1);
  const [param, setParam] = useState({});

  const homepageLandingUrl = '/landing/page';

  useEffect(() => {
    const _param: any = {
      pageNo: page,
      perPage: config.pageSize,
    };

    if (query.page) {
      _param.pageNo = Number(query.page);
      setPage(Number(query.page));
    }
    if (query.title) {
      _param.title = String(query.title);
      setField('title');
      setKeyword(String(query.title));
    }

    execute(getLandings(_param));
    _param.page = _param.pageNo;
    delete _param.pageNo;
    delete _param.perPage;
    setParam(_param);
  }, [location]);

  const onSearch = () => {
    const param: any = { page: 1 };
    switch (field) {
      case 'title':
        param.title = keyword;
        break;
      case 'regMemName':
        param.regMemName = keyword;
        break;
    }

    history.push(`${homepageLandingUrl}?${QueryString.stringify(param)}`);
  };

  const columns: TableColumnsType<ILandingItem> = [
    {
      title: t('landing.imagemanage.column1'),
      dataIndex: 'landingCode',
      key: 'landingCode',
      align: 'center',
      sorter: (a, b) => a.landingCode - b.landingCode,
      render: (value, record) => {
        return value;
      },
    },
    {
      title: t('landing.imagemanage.column2'),
      dataIndex: 'landingstatus',
      key: 'landingstatus',
      align: 'center',
      sorter: (a, b) => isRunnable(a).localeCompare(isRunnable(b)),
      render: (value, record) => isRunnable(record),
    },
    {
      title: t('landing.imagemanage.column3'),
      dataIndex: 'openYn',
      key: 'openYn',
      align: 'center',
      sorter: (a, b) => a.openYn?.localeCompare(b.openYn),
      render: (value) =>
        value === 'Y' ? t('common.text.public') : t('common.text.private'),
    },
    {
      title: t('landing.imagemanage.column4'),
      dataIndex: 'title',
      key: 'title',
      align: 'center',
      sorter: (a, b) => a.title?.localeCompare(b.title),
      render: value => {
        return (<div className='text-left ant-table-cell-ellipsis'>{value}</div>);
      }
    },
    {
      title: t('landing.imagemanage.column5'),
      dataIndex: 'memberName',
      key: 'memberName',
      align: 'center',
      sorter: (a, b) => a.memberName?.localeCompare(b.memberName),
    },
    {
      title: t('landing.imagemanage.column6'),
      dataIndex: 'openYn',
      key: 'openYn',
      align: 'center',
      sorter: (a, b) => a.startDate?.localeCompare(b.startDate),
      render: (value, record) => {
        return `${moment(record.startDate).format('YYYY.MM.DD')} ~ ${moment(
          record.endDate,
        ).format('YYYY.MM.DD')}`;
      },
    },
    {
      title: t('landing.imagemanage.column7'),
      dataIndex: 'firstRegDttm',
      key: 'firstRegDttm',
      align: 'center',
      sorter: (a, b) => a.firstRegDttm?.localeCompare(b.firstRegDttm),
      render: (value) => moment(value).format('YYYY.MM.DD'),
    },
  ];

  const isRunnable = (record: ILandingItem) => {
    let landingStatus = t('landing.imagemanage.status1');
    if (serverDate && record.openYn === 'Y') {
      const isBetween = moment(serverDate).isBetween(
        moment(`${record.startDate} ${record.startTime}`, 'YYYYMMDD HH:mm:ss'),
        moment(`${record.endDate} ${record.endTime}`, 'YYYYMMDD HH:mm:ss'),
      );

      if (isBetween) {
        landingStatus = t('landing.imagemanage.status2');
      }
    }

    return landingStatus;
  };

  return (
    <>
    <SimpleHeader
      iconFile="icon-homepage-big.svg"
      parentMenu={t('homepage.landing.title1')}
      currentMenu={t('homepage.landing.title2')}
      onlyTitle={true}
    />
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <div>
              <Select
                placeholder={t('common.button.select')}
                value={field}
                onChange={(value) => setField(value)}
              >
                <Select.Option value="title">
                  {t('common.column.title')}
                </Select.Option>
              </Select>
            </div>
            <input
              type="text"
              className="form-control w-240p"
              placeholder={t('homepage.notice.placeholder1')}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') onSearch();
              }}
            />
            <div className="comm-btn__area">
              <Button
                className="btn-basic small black"
                onClick={onSearch}
                text={t('common.button.search')}
              />
            </div>
          </li>
        </ul>
      </div>
    </div>
    <div className="content scrollbar">
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      
      <div className="col-frame">
        <div className="comm-bx__between">
          <div className="comm-sch_result">
            {t('common.text.count')}: <b>{res?.total}</b>
            {t('common.text.unit')}
          </div>
          <div className="comm-btn__area">
            <Button
              className="btn-basic pink"
              onClick={() => history.push(`${homepageLandingUrl}/create`)}
              text={t('event.education.button1')}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
        <div className='comm-table__area'>
          <Table
            columns={columns}
            dataSource={res?.data}
            pagination={{
              current: page,
              pageSize: config.pageSize,
            }}
            onRow={(record) => {
              return {
                onClick: () => {
                  history.push(
                    `${homepageLandingUrl}/${record.landingCode}${location.search}`,
                  );
                },
              };
            }}
            rowClassName="pointer"
            rowKey="landingCode"
          />
        </div>
        <div className="comm-bx__between">
          <div />
          <div className="common-paging center">
            <Pagination
              count={res?.total || 0}
              page={page}
              param={param}
              path={homepageLandingUrl}
            />
          </div>
          <div className="comm-btn__area right">
            <Button
              className="btn-basic pink"
              onClick={() => history.push(`${homepageLandingUrl}/create`)}
              text={t('event.education.button1')}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default HomepageLanding;
